import React, { useState, useEffect, useCallback, useRef } from 'react';
import Graph from './Graph';
import { investigationService } from '../services/api';
import { formatDate } from '../services/dateUtils';
import '../styles/TxLookupPanel.css';

export default function TxLookupPanel({ investigation }) {
  const [pasteText, setPasteText]       = useState('');
  const [loading, setLoading]           = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [promoting, setPromoting]       = useState(false);
  const [result, setResult]             = useState(null);
  const [error, setError]               = useState(null);
  const [promoted, setPromoted]         = useState(false);
  const graphRef = useRef(null);

  // ── Load any previously saved seed TX lookup ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await investigationService.getTxLookupGraph(investigation.id);
        if (cancelled) return;
        if (res.data?.tx_ids?.length > 0) {
          setPasteText(res.data.tx_ids.join('\n'));
          setResult({
            graph:        res.data.graph,
            transactions: res.data.transactions || [],
            errors:       res.data.errors || [],
            fetched:      (res.data.transactions || []).length,
            failed:       (res.data.errors || []).length,
            dateWindow:   res.data.date_window || null,
          });
        }
      } catch (_) {
        // No saved data — fine
      } finally {
        if (!cancelled) setLoadingSaved(false);
      }
    })();
    return () => { cancelled = true; };
  }, [investigation.id]);

  // Parse paste area into clean TX IDs
  const parseTxIds = useCallback((text) => {
    return text
      .split(/[\n,;\s]+/)
      .map(s => s.trim())
      .filter(s => s.length > 10);
  }, []);

  const txIds = parseTxIds(pasteText);

  const handleFetch = async () => {
    if (txIds.length === 0) { setError('Paste at least one transaction ID.'); return; }
    setError(null);
    setLoading(true);
    setResult(null);
    setPromoted(false);
    try {
      const res = await investigationService.fetchByTxIds(investigation.id, { tx_ids: txIds });
      setResult(res.data);
    } catch (err) {
      setError('Fetch failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Promote seed TX graph → main investigation graph
  const handlePromoteToMain = async () => {
    if (!result?.graph) return;
    if (!window.confirm('Set this seed TX graph as the main investigation graph? This will replace the current main graph.')) return;
    setPromoting(true);
    try {
      await investigationService.setGraphFromSeedTxs(investigation.id);
      setPromoted(true);
    } catch (err) {
      setError('Failed to promote graph: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPromoting(false);
    }
  };

  const handleClear = () => {
    setPasteText('');
    setResult(null);
    setError(null);
    setPromoted(false);
  };

  const handleScrollToGraph = () => {
    if (graphRef.current) graphRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Date window calculation from fetched transactions ──
  const dateWindow = (() => {
    if (!result?.transactions?.length) return null;
    const timestamps = result.transactions
      .map(tx => tx.timestamp ? new Date(tx.timestamp).getTime() : null)
      .filter(Boolean);
    if (!timestamps.length) return null;
    const earliest = new Date(Math.min(...timestamps));
    const latest   = new Date(Math.max(...timestamps));
    const windowEnd = new Date(latest);
    windowEnd.setDate(windowEnd.getDate() + 30);
    return { earliest, latest, windowEnd };
  })();

  if (loadingSaved) return <div className="txlookup-loading">Loading saved seed transactions…</div>;

  return (
    <div className="txlookup-panel">

      {/* ── Page header ── */}
      <div className="txlookup-header">
        <div className="txlookup-header-left">
          <h2>🌱 Seed Transactions</h2>
          <p className="txlookup-subtitle">
            Paste the crime transaction IDs — payments sent from victim wallets to the scammer's
            wallet. Only these transactions appear on the graph; all other wallet history is excluded as noise.
          </p>
        </div>
        {result && (
          <div className="txlookup-header-pills">
            <div className="txlookup-summary-pill ok">✅ {result.fetched} fetched</div>
            {result.failed > 0 && (
              <div className="txlookup-summary-pill err">❌ {result.failed} failed</div>
            )}
            {result.graph?.nodes?.length > 0 && (
              <button className="txlookup-pill-btn" onClick={handleScrollToGraph}>
                🔗 View Graph ↓
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Two-column body ── */}
      <div className="txlookup-body">

        {/* LEFT — input form */}
        <div className="txlookup-left">
          <div className="txlookup-form">
            <div className="txlookup-paste-section">
              <label>
                Transaction IDs
                <span className="opt-label">
                  {txIds.length > 0 ? ` — ${txIds.length} detected` : ' — paste one per line'}
                </span>
              </label>
              <textarea
                className="txlookup-paste-area"
                placeholder={'Paste TX IDs here, one per line:\n\nTV1zeU4bK92Fs...\n0x4a9f8c2b1d...\nabc123def456...'}
                value={pasteText}
                onChange={e => setPasteText(e.target.value)}
                rows={10}
                spellCheck={false}
              />
            </div>

            {txIds.length > 0 && (
              <div className="txlookup-detected-ids">
                <span className="txlookup-detected-label">Detected TX IDs:</span>
                <div className="txlookup-id-chips">
                  {txIds.map((id, i) => (
                    <span key={i} className="txlookup-id-chip">
                      {id.slice(0, 8)}…{id.slice(-6)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {error && <div className="txlookup-error">{error}</div>}

            <div className="txlookup-actions">
              <button
                className="txlookup-btn-fetch"
                onClick={handleFetch}
                disabled={loading || txIds.length === 0}
              >
                {loading
                  ? '⏳ Fetching from blockchain…'
                  : `🔍 Fetch ${txIds.length > 0 ? txIds.length : ''} Transaction${txIds.length !== 1 ? 's' : ''}`}
              </button>
              <button className="txlookup-btn-clear" onClick={handleClear} disabled={loading}>
                Clear
              </button>
            </div>
          </div>

          {/* Investigation info card under the form */}
          <div className="txlookup-inv-card">
            <div className="txlookup-inv-row">
              <span className="txlookup-inv-label">Blockchain</span>
              <strong className="txlookup-inv-value">{investigation.blockchain?.toUpperCase()}</strong>
            </div>
            <div className="txlookup-inv-row">
              <span className="txlookup-inv-label">Start Address</span>
              <code className="txlookup-inv-addr">{investigation.start_address}</code>
            </div>
          </div>
        </div>

        {/* RIGHT — crime window + transaction table */}
        <div className="txlookup-right">
          {!result ? (
            <div className="txlookup-right-empty">
              <div className="txlookup-right-empty-icon">📋</div>
              <p>Fetch transactions to see the crime window and transaction breakdown here.</p>
            </div>
          ) : (
            <>
              {/* Crime window */}
              {dateWindow && (
                <div className="txlookup-date-window">
                  <div className="txlookup-date-window-title">📅 Crime Window</div>
                  <div className="txlookup-date-window-grid">
                    <div className="txlookup-date-item">
                      <span className="txlookup-date-label">First crime TX</span>
                      <span className="txlookup-date-value">{formatDate(dateWindow.earliest)}</span>
                    </div>
                    <div className="txlookup-date-item">
                      <span className="txlookup-date-label">Last crime TX</span>
                      <span className="txlookup-date-value">{formatDate(dateWindow.latest)}</span>
                    </div>
                    <div className="txlookup-date-item highlight">
                      <span className="txlookup-date-label">Trace window closes</span>
                      <span className="txlookup-date-value">{formatDate(dateWindow.windowEnd)}</span>
                      <span className="txlookup-date-note">last TX + 30 days</span>
                    </div>
                  </div>
                  <p className="txlookup-window-hint">
                    Only outbound transactions from the scammer's wallet within this window are relevant — everything outside is noise.
                  </p>
                </div>
              )}

              {/* Failed lookups */}
              {result.errors?.length > 0 && (
                <div className="txlookup-error-list">
                  <h4>Failed lookups</h4>
                  {result.errors.map((e, i) => (
                    <div key={i} className="txlookup-error-row">
                      <code className="txlookup-hash-code">{e.tx_hash}</code>
                      <span className="txlookup-error-reason">{e.error}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Transaction table */}
              {result.transactions?.length > 0 && (
                <div className="txlookup-tx-table-wrap">
                  <h3>Crime Transactions</h3>
                  <div className="txlookup-table-scroll">
                    <table className="txlookup-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>TX Hash</th>
                          <th>Date</th>
                          <th>From (Victim)</th>
                          <th>To (Scammer)</th>
                          <th>Amount</th>
                          <th>Token</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.transactions.map((tx, i) => (
                          <tr key={i}>
                            <td className="txlookup-td-num">{i + 1}</td>
                            <td>
                              <code className="txlookup-hash-code txlookup-hash-short" title={tx.tx_hash}>
                                {tx.tx_hash ? tx.tx_hash.slice(0, 10) + '…' + tx.tx_hash.slice(-8) : '—'}
                              </code>
                            </td>
                            <td className="txlookup-td-date">
                              {tx.timestamp ? formatDate(tx.timestamp) : '—'}
                            </td>
                            <td>
                              <code className="txlookup-addr-code victim" title={tx.from_address}>
                                {tx.from_address ? tx.from_address.slice(0, 8) + '…' + tx.from_address.slice(-6) : '—'}
                              </code>
                            </td>
                            <td>
                              <code className="txlookup-addr-code scammer" title={tx.to_address}>
                                {tx.to_address ? tx.to_address.slice(0, 8) + '…' + tx.to_address.slice(-6) : '—'}
                              </code>
                            </td>
                            <td className="txlookup-td-value">
                              {tx.value != null
                                ? Number(tx.value).toLocaleString(undefined, { maximumFractionDigits: 6 })
                                : '—'}
                            </td>
                            <td>{tx.token_symbol || '—'}</td>
                            <td>
                              <span className={`txlookup-status ${tx.status === 'success' ? 'ok' : 'fail'}`}>
                                {tx.status || '—'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {result.transactions.length > 1 && (
                        <tfoot>
                          <tr>
                            <td colSpan={5} className="txlookup-td-total-label">Total received by scammer</td>
                            <td className="txlookup-td-value txlookup-td-total">
                              {result.transactions
                                .reduce((sum, tx) => sum + (tx.value || 0), 0)
                                .toLocaleString(undefined, { maximumFractionDigits: 6 })}
                            </td>
                            <td>{result.transactions[0]?.token_symbol || ''}</td>
                            <td />
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              )}

              {result.fetched === 0 && result.failed === 0 && (
                <div className="txlookup-empty">No transactions returned.</div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Graph — full width below both columns ── */}
      {result?.graph?.nodes?.length > 0 && (
        <div className="txlookup-graph-section" ref={graphRef}>
          <div className="txlookup-graph-header">
            <div>
              <h3>Seed Transaction Graph</h3>
              <p className="txlookup-graph-note">
                {result.graph.nodes.length} addresses · {result.graph.edges.length} transactions · crime TXs only
              </p>
            </div>
            <div className="txlookup-promote-area">
              {promoted ? (
                <div className="txlookup-promoted-badge">✅ Set as main graph</div>
              ) : (
                <button
                  className="txlookup-btn-promote"
                  onClick={handlePromoteToMain}
                  disabled={promoting}
                >
                  {promoting ? '⏳ Setting…' : '📌 Set as Investigation Graph'}
                </button>
              )}
              <p className="txlookup-promote-hint">Replaces the Graph tab with only these seed transactions</p>
            </div>
          </div>
          <div className="txlookup-graph-container">
            <Graph data={result.graph} startAddress={investigation.start_address} />
          </div>
        </div>
      )}

    </div>
  );
}
